/**
 * Chart colours and the shared parts of every chart option.
 *
 * Stance is a diverging scale - negative, neutral, positive - so it is two hues around a neutral grey
 * midpoint, and never a rainbow. The steps were checked with the data-visualisation skill's validator
 * against this app's dark surface: every adjacent pair separates for normal vision (ΔE >= 15) and for
 * red-green colour blindness (ΔE >= 8), and all five clear 3:1 contrast. The validator also reports its
 * categorical-only rules (a chroma floor and a lightness band) as failures here, which a diverging
 * scale is meant to break: its midpoint has to read as grey.
 *
 * Colour is never the only signal: every chart carries a legend, a tooltip and a table view.
 */

const STANCE = {
  "-2": "#ff8f75",
  "-1": "#bf3f37",
  "0": "#86857e",
  "1": "#2b8ae8",
  "2": "#a5d2ff",
} as const;

export const SURFACE = "#16181d"; // the panel the charts sit on; also the gap between stacked marks
export const INK = "#c7d3e0";
export const MUTED = "#8b98a8";
export const GRID = "#262a31";
export const EDGE = "#4a5361"; // network links: visible without competing with the personas
export const SERIES = "#3987e5"; // one-series charts take the brand colour

export const STANCE_ORDER = [-2, -1, 0, 1, 2] as const;

/** Colour for a stance from -2 to +2; grey for a persona who never posted. */
export function stanceColour(stance: number | null | undefined): string {
  if (stance === null || stance === undefined) return GRID;
  return STANCE[String(stance) as keyof typeof STANCE] ?? MUTED;
}

/** Negative / Neutral / Positive, taken from the ends and middle of the same scale. */
export const CATEGORY_COLOURS: Record<string, string> = {
  Negative: STANCE["-1"],
  Neutral: STANCE["0"],
  Positive: STANCE["1"],
};

const FONT = "Geist Variable, system-ui, -apple-system, 'Segoe UI', sans-serif";

/** Axes, grid, tooltip and legend, the same on every chart. */
export function base() {
  return {
    backgroundColor: "transparent",
    textStyle: { fontFamily: FONT, color: INK },
    animationDuration: 300,
    // Room under the plot for the axis title, with the legend below that - they collided at first.
    grid: { left: 8, right: 16, top: 8, bottom: 52, containLabel: true },
    tooltip: {
      backgroundColor: "#0f1115",
      borderColor: GRID,
      textStyle: { color: INK, fontFamily: FONT, fontSize: 12 },
      extraCssText: "box-shadow: 0 4px 16px rgb(0 0 0 / 0.45); max-width: 320px; white-space: normal;",
    },
    legend: {
      bottom: 0,
      itemGap: 14,
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: MUTED, fontFamily: FONT, fontSize: 11 },
    },
  };
}

/** A recessive axis: labels and line in muted ink, grid lines barely there. */
export function axis(options: { percent?: boolean; title?: string; category?: boolean } = {}) {
  return {
    type: options.category ? "category" : "value",
    name: options.title,
    nameLocation: "middle" as const,
    nameGap: 26,
    nameTextStyle: { color: MUTED, fontSize: 11 },
    axisLabel: {
      color: MUTED,
      fontSize: 11,
      ...(options.percent ? { formatter: (value: number) => `${Math.round(value * 100)}%` } : {}),
    },
    axisLine: { lineStyle: { color: GRID } },
    axisTick: { show: false },
    splitLine: { show: !options.category, lineStyle: { color: GRID } },
  };
}

/** Stacked and adjacent fills get a 2px surface-coloured gap, so segments read as separate marks. */
export const SEGMENT_GAP = { borderColor: SURFACE, borderWidth: 2 };

export const percent = (share: number) => `${Math.round(share * 100)}%`;
